// src/services/userService.js
import { repo } from "../dbservice/repo";
import { UserModel } from "../models/UserModel";
import {
  ROLES,
  ROLE_LABELS,
  normalizeRole,
  getAssignableRoles as getRolesForScope,
  canAssignRole,
  hasRequiredRole,
} from "../utils/roles";
import { singleValueListService } from "./singleValueListService";

class UserService {
  constructor() {
    this.collectionName = "users";
  }

  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("Failed to parse userData from localStorage", e);
      return null;
    }
  }

  // ────────────────────────────────────────────────────────────
  //  ROLE CATALOG & PERMISSIONS
  // ────────────────────────────────────────────────────────────

  /**
   * Fetches all available roles.
   * Priority: DB (`user_roles` list in singleValueListService) -> Fallback: `roles.js`
   */
  async getAllRoles() {
    try {
      const items = await singleValueListService.getItems("user_roles");
      if (Array.isArray(items) && items.length > 0) {
        return items.map((it) => {
          const value = normalizeRole(it.value);
          const label = it.label || ROLE_LABELS[value] || value;
          return { value, label };
        });
      }
    } catch (err) {
      console.warn("[UserService] getAllRoles read failed, using system defaults:", err);
    }

    // Fallback: derive directly from roles.js
    return Object.values(ROLES).map((roleKey) => ({
      value: roleKey,
      label: ROLE_LABELS[roleKey] || roleKey,
    }));
  }

  /**
   * Returns assignable roles for the given caller (or current user).
   */
  getAssignableRoles(caller = null) {
    const user = caller && typeof caller === "object" ? caller : null;
    const rawRole = typeof caller === "string" ? caller : user?.role || this.currentUser?.role;

    return getRolesForScope(rawRole); // [{ value, label }, ...]
  }

  /**
   * Async variant that respects any label overrides stored in DB.
   */
  async getAssignableRolesAsync(caller = null) {
    const base = this.getAssignableRoles(caller);
    try {
      const catalog = await this.getAllRoles();
      const byValue = new Map(catalog.map((r) => [r.value, r.label]));
      return base.map((r) => ({
        value: r.value,
        label: byValue.get(r.value) || r.label,
      }));
    } catch {
      return base;
    }
  }

  getRoleByValue(value) {
    const normalized = normalizeRole(value);
    return {
      value: normalized,
      label: ROLE_LABELS[normalized] || normalized,
    };
  }

  canAssign(callerRole, targetRole) {
    return canAssignRole(callerRole, targetRole);
  }

  hasRole(userRole, allowedRoles) {
    return hasRequiredRole(userRole, allowedRoles);
  }

  // ────────────────────────────────────────────────────────────
  //  PROFILE / CRUD
  // ────────────────────────────────────────────────────────────

  buildFallbackProfile(firebaseUser, { degraded = false } = {}) {
    if (!firebaseUser) return null;
    const email = firebaseUser.email || "";
    const id = firebaseUser.uid || firebaseUser.id || "";
    const name =
      firebaseUser.displayName ||
      firebaseUser.name ||
      email.split("@")[0] ||
      "Unknown User";

    return {
      id,
      userId: id,
      uid: id,
      email,
      name,
      role: ROLES.USER,
      companyId: null,
      departmentId: null,
      locationCode: null,
      description: null,
      status: "active",
      metadata: {
        createdAt: null,
        createdBy: null,
        deletedFlag: false,
        modifiedAt: null,
        modifiedBy: null,
      },
      ...(degraded ? { _degraded: true } : {}),
    };
  }

  async buildUserProfile(firebaseUser, { degraded = false } = {}) {
    if (!firebaseUser?.uid) {
      throw new Error("[UserService] firebaseUser is null or missing uid.");
    }
    try {
      const dbUser = await this.fetchUserById(firebaseUser.uid);
      if (dbUser) {
        return {
          ...dbUser,
          uid: firebaseUser.uid,
          id: firebaseUser.uid,
          email: firebaseUser.email,
          role: normalizeRole(dbUser.role),
        };
      }
      console.warn("[UserService] No DB record for UID:", firebaseUser.uid);
      return this.buildFallbackProfile(firebaseUser, { degraded });
    } catch (err) {
      console.error("[UserService] Profile build failed:", err);
      return this.buildFallbackProfile(firebaseUser, { degraded: true });
    }
  }

  async fetchUsers({
    currentUser = null,
    companyId = "",
    departmentId = "",
    locationCode = "",
    role = "",
    status = "",
    includeDeleted = false,
  } = {}) {
    const user = currentUser || this.currentUser;
    if (!user || !user.role) {
      console.warn("[UserService] fetchUsers: no acting user context");
      return [];
    }

    const callerRole = normalizeRole(user.role);
    const isSuperAdmin = callerRole === ROLES.SUPERADMIN;
    const filters = [];

    if (!isSuperAdmin) {
      if (!user.companyId) {
        throw new Error("Access denied: user has no company context.");
      }
      filters.push({ field: "companyId", value: String(user.companyId) });
    } else if (companyId) {
      filters.push({ field: "companyId", value: String(companyId) });
    }

    if (departmentId) filters.push({ field: "departmentId", value: String(departmentId) });
    if (locationCode) filters.push({ field: "locationCode", value: String(locationCode) });
    if (role)         filters.push({ field: "role",         value: normalizeRole(role) });
    if (status)       filters.push({ field: "status",       value: status });

    try {
      let users = await repo.fetchData(this.collectionName, filters, []);
      if (!Array.isArray(users)) users = [];
      if (!includeDeleted) {
        users = users.filter((u) => !u.metadata?.deletedFlag);
      }
      return users;
    } catch (error) {
      console.error(`[UserService] fetchUsers failed:`, error);
      return [];
    }
  }

  async fetchUserById(id) {
    if (!id) {
      console.error("fetchUserById: User ID missing");
      return null;
    }
    try {
      const userDoc = await repo.fetchById(this.collectionName, id);
      if (!userDoc) {
        console.warn(`[UserService] No user document matches ID: ${id}`);
        return null;
      }
      return {
        ...userDoc,
        userId: userDoc.userId || userDoc.id,
        name: userDoc.name || "Unknown User",
        role: normalizeRole(userDoc.role) || ROLES.USER,
        companyId: userDoc.companyId || "",
        departmentId: userDoc.departmentId || "",
        locationCode: userDoc.locationCode || "",
      };
    } catch (error) {
      console.error(`Failed to fetch user profile for ID ${id}:`, error);
      return null;
    }
  }

  async createUser(rawPayload, actor = {}) {
    const targetUserId = rawPayload?.id || rawPayload?.uid;
    if (!targetUserId) throw new Error("User ID is required for creation.");
    return await this.upsertData(targetUserId, rawPayload, { 
      isCreation: true, 
      createdByRole: actor?.role || null 
    });
  }

  async upsertData(targetUserId, data, { isCreation = false, createdByRole = null } = {}) {
    if (!targetUserId) throw new Error("Target User ID is required");
    // Use explicitly passed role, or infer from data/metadata, or fallback to data
    const callerRole = createdByRole || data.createdByRole || data.role;
    const userInstance = new UserModel({ ...data, id: targetUserId }, { isCreation, createdByRole: callerRole });
    if (!userInstance.isValid()) {
      throw new Error("Invalid user payload structure or field formats.");
    }
    return await repo.upsertData(this.collectionName, targetUserId, userInstance.toPayload());
  }

  async deleteUser(id, currentUser = null) {
    if (!id) throw new Error("User ID is required");
    const user = currentUser || this.currentUser;
    const nowIso = new Date().toISOString();
    const actor = user?.email || user?.uid || user?.id || "system";

    try {
      const existing = await repo.fetchById(this.collectionName, id);
      const instance = new UserModel(
        {
          id,
          status: "inactive",
          metadata: {
            ...(existing?.metadata || {}),
            deletedFlag: true,
            modifiedAt: nowIso,
            modifiedBy: actor,
          },
        },
        { isCreation: false }
      );
      if (!instance.isValid()) throw new Error("Invalid user payload on delete.");
      await repo.upsertData(this.collectionName, id, instance.toPayload());
      return { success: true };
    } catch (error) {
      console.error(`Failed to soft-delete user ${id}:`, error);
      throw error;
    }
  }
}

export const userService = new UserService();